import { useEffect, useState } from 'react';
import { accountPref, setAccountPref } from '../../lib/boardNav';
import { cx } from '../ui/ui';
import { LibraryBrowser } from '../library/LibraryBrowser';
import { MediaView } from './MediaView';
import { useLibrary } from '../../lib/library';

type Tab = 'cards' | 'library' | 'team';

/**
 * „Dateien“ eines Boards (0.54): ein Einstieg, zwei Bereiche – Kartenanhänge (Dateien an Aufgaben)
 * und Bibliothek (bewusst dauerhaft abgelegt). Gäste sehen nur Kartenanhänge.
 * 0.57: Bei Boards eines Teams zusätzlich „Team“ – die für dieses Board freigegebenen Ordner der
 * Team-Bibliothek (an Karten hängen über „Aus Bibliothek“); 0.72: Teammitglieder schalten dort auf die ganze
 * Team-Bibliothek um und bearbeiten sie direkt.
 */
export function FilesView({
  boardId,
  teamId,
  canEdit,
  guest,
  onOpenCard,
}: {
  boardId: string;
  teamId: string | null;
  canEdit: boolean;
  guest: boolean;
  onOpenCard: (id: string) => void;
}) {
  const [pref, setTab] = useState<Tab>(() => (guest ? 'cards' : accountPref<Tab>('filesTab', 'cards')));
  const tab: Tab = pref === 'team' && !teamId ? 'library' : pref;
  useEffect(() => {
    if (!guest) setAccountPref('filesTab', tab);
  }, [tab, guest]);
  const tabs: { id: Tab; label: string }[] = [
    { id: 'cards', label: 'Kartenanhänge' },
    { id: 'library', label: 'Bibliothek' },
    ...(teamId ? [{ id: 'team' as const, label: 'Team' }] : []),
  ];
  return (
    <div className="flex flex-col">
      {!guest && (
        <div role="tablist" aria-label="Dateien" className="mx-auto flex w-full max-w-6xl gap-1 px-3 pt-2 sm:px-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              id={`files-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`files-panel-${t.id}`}
              onClick={() => setTab(t.id)}
              className={cx('min-h-11 rounded-lg px-4 text-sm font-medium sm:min-h-9', tab === t.id ? 'bg-accent text-on-accent' : 'hover:bg-black/5 dark:hover:bg-white/10')}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div role={guest ? undefined : 'tabpanel'} id={`files-panel-${tab}`} aria-labelledby={guest ? undefined : `files-tab-${tab}`}>
        {tab === 'library' && !guest ? (
          <div className="mx-auto w-full max-w-6xl px-3 pt-2 pb-10 sm:px-4">
            <LibraryBrowser key="board" scope={{ kind: 'board', boardId }} canEdit={canEdit} onOpenCard={onOpenCard} />
          </div>
        ) : tab === 'team' && !guest && teamId ? (
          <TeamFiles boardId={boardId} teamId={teamId} onOpenCard={onOpenCard} />
        ) : (
          <MediaView boardId={boardId} onOpenCard={onOpenCard} />
        )}
      </div>
    </div>
  );
}

/**
 * 0.72: Team-Bibliothek im Board. Alle sehen die für dieses Board freigegebenen Ordner; Teammitglieder können
 * hier auch auf die ganze Team-Bibliothek umschalten – mit Hochladen, Ordnern, Umbenennen und (Admins)
 * Freigeben, je nach Teamrechten –, ohne das Board zu verlassen. Die Wahl merkt sich das Gerät.
 */
function TeamFiles({ boardId, teamId, onOpenCard }: { boardId: string; teamId: string; onOpenCard: (id: string) => void }) {
  const shared = useLibrary({ kind: 'boardTeam', boardId }, { folder: null, q: '', trash: false, type: 'all' });
  const member = !!shared.data?.teamMember;
  const [mode, setModeState] = useState<'board' | 'team'>(() => accountPref<'board' | 'team'>('teamFilesMode', 'board'));
  const setMode = (m: 'board' | 'team') => {
    setModeState(m);
    setAccountPref('teamFilesMode', m);
  };
  const whole = member && mode === 'team';
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-3 pt-2 pb-10 sm:px-4">
      {member && (
        <div role="group" aria-label="Team-Dateien" className="flex w-fit flex-wrap rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
          {(
            [
              ['board', 'Für dieses Board freigegeben'],
              ['team', 'Ganze Team-Bibliothek'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={(k === 'team') === whole}
              onClick={() => setMode(k)}
              className={cx('min-h-10 rounded-md px-3 text-sm sm:min-h-9', (k === 'team') === whole ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)]')}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {whole ? (
        <LibraryBrowser key="team-all" scope={{ kind: 'team', teamId }} onOpenCard={onOpenCard} />
      ) : (
        <LibraryBrowser key="team" scope={{ kind: 'boardTeam', boardId }} teamId={teamId} onOpenCard={onOpenCard} onOpenTeamLibrary={member ? () => setMode('team') : undefined} />
      )}
    </div>
  );
}
